import { useCallback, useEffect, useState } from "react";
import { fetchHospitalInvoices } from "../api/hospitalApi";
import type { CompanyBillingInvoice } from "../api/types";
import { hospitalInvoices } from "../data";
import { getAuthToken } from "../auth";
import type { InvoiceDisplayStatus } from "../types";

import { resolveInvoiceNatureKey } from "../components/InvoiceNatureBadge";

export type HospitalInvoiceRow = {
  id: string;
  displayId: number;
  reference: string;
  hospital: string;
  company: string;
  fileName: string;
  amount: number;
  submittedDate: string;
  displayStatus: Extract<InvoiceDisplayStatus, "submitted" | "inReview" | "approved">;
  invoiceNature?: "forfait_mensuel" | "hors_forfait" | null;
  isForfaitMensuel?: boolean;
};

function mapApiInvoice(row: CompanyBillingInvoice): HospitalInvoiceRow {
  const created = row.created_at ? new Date(row.created_at) : null;
  const submittedDate = created ? created.toLocaleDateString("fr-FR") : row.period_label;
  const nature = resolveInvoiceNatureKey({
    invoiceNature: row.invoice_nature,
    isForfaitMensuel: row.is_forfait_mensuel,
  });

  return {
    id: `hospital-inv-${row.id}`,
    displayId: row.id,
    reference: row.reference,
    hospital: row.hospital,
    company: row.company || row.label,
    fileName: row.label || row.reference,
    amount: row.amount_usd,
    submittedDate,
    displayStatus:
      row.display_status === "approved"
        ? "approved"
        : row.display_status === "submitted"
          ? "submitted"
          : "inReview",
    invoiceNature: nature,
    isForfaitMensuel: nature === "forfait_mensuel",
  };
}

function mapDemoInvoice(invoice: (typeof hospitalInvoices)[number]): HospitalInvoiceRow {
  const displayStatus =
    invoice.status === "approved"
      ? "approved"
      : invoice.reviewStatus === "submitted"
        ? "submitted"
        : "inReview";

  return {
    id: invoice.id,
    displayId: invoice.displayId,
    reference: invoice.reference,
    hospital: invoice.hospital,
    company: invoice.company,
    fileName: invoice.fileName,
    amount: invoice.amount,
    submittedDate: invoice.submittedDate,
    displayStatus,
  };
}

export function useHospitalInvoices() {
  const [invoices, setInvoices] = useState<HospitalInvoiceRow[]>(() =>
    hospitalInvoices.map(mapDemoInvoice),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setInvoices(hospitalInvoices.map(mapDemoInvoice));
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const rows = await fetchHospitalInvoices();
      setInvoices(rows.map(mapApiInvoice));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger les factures.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { invoices, loading, error, usingApi, reload: load };
}
